import React, { useMemo, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Check, Eye, EyeOff } from 'lucide-react';
import { FormField } from 'src/components/common/FormField';
import OTPVerificationSection from './OTPVerificationSection';

export const OTPVerification = ({
  type = 'contact', // 'contact' or 'email'
  register,
  errors,
  value,
  verified,
  isOTPSent,
  isOTPLoading,
  showOTPInput,
  isVerifyingOTP,
  otpError,
  canResend,
  resendTimeout,
  handleSendOTP,
  handleVerifyOTP,
  currentOTP,
  otpFocusTrigger,
  disabled = false,
  readOnly = false,
  // New props for email linking flow
  showEmailOnly = false, // Show only email input with send button
  showPasswordFields = false, // Show password fields along with OTP
  showChangeEmail = false, // Show "Change" link for email
  onChangeEmail = null, // Callback for changing email
  passwordRegister = null, // Register function for password fields
  showPassword = false,
  showConfirmPassword = false,
  onTogglePassword = null,
  onToggleConfirmPassword = null,
  submitButtonText = 'Submit',
  onSubmit = null,
}) => {
  const config = {
    contact: {
      label: 'Contact',
      placeholder: 'Enter phone number',
      validation: {
        required: 'Contact number is required',
        pattern: {
          value: /^[0-9]{10}$/,
          message: 'Invalid contact number',
        },
      },
    },
    email: {
      label: 'Email',
      placeholder: 'Enter email address',
      validation: {
        required: 'Email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address',
        },
      },
    },
  };

  const registerProps = register(type, config[type].validation);

  // Email linking flow - Step 1: Show only email input
  if (showEmailOnly && type === 'email') {
    return (
      <div className="space-y-4">
        <FormField
          className="text-xs"
          label="Email address"
          id="email"
          error={errors[type]?.message}
        >
          <Input
            id={type}
            onChange={registerProps.onChange}
            onBlur={registerProps.onBlur}
            name={registerProps.name}
            ref={registerProps.ref}
            className={`${errors[type] ? 'border-red-500' : ''} text-xs`}
            placeholder={config[type].placeholder}
            disabled={disabled}
            readOnly={readOnly}
            type="email"
          />
        </FormField>

        {value && (
          <div className="text-xs text-gray-600 mb-4">
            We&apos;ll send a 4-digit code to verify you own this email.
          </div>
        )}

        <Button
          type="button"
          onClick={() => handleSendOTP('SEND')}
          disabled={!value || isOTPLoading || disabled}
          className="w-fit bg-ah-brand-primary hover:bg-ah-brand-primary-hover text-white"
        >
          {isOTPLoading ? 'Sending...' : 'Send code'}
        </Button>
      </div>
    );
  }

  // Email linking flow - Step 2: Show password fields + OTP
  if (showPasswordFields && type === 'email') {
    // Local state to drive dynamic password rule indicators
    const [localPassword, setLocalPassword] = useState('');
    const [showRules, setShowRules] = useState(false);

    const passwordRules = useMemo(
      () => ({
        minLength: localPassword.length >= 8,
        upper: /[A-Z]/.test(localPassword),
        lower: /[a-z]/.test(localPassword),
        number: /\d/.test(localPassword),
        symbol: /[@$!%*?&]/.test(localPassword),
        noSpaces: !/\s/.test(localPassword),
      }),
      [localPassword]
    );

    const passwordReg = passwordRegister('password', {
      required: 'Password is required',
      minLength: {
        value: 8,
        message: 'Password must be at least 8 characters',
      },
    });
    const { onChange: onPasswordRHFChange, ...passwordRegRest } = passwordReg;

    return (
      <div className="space-y-4">
        {showChangeEmail && (
          <div className="text-xs text-gray-600 mb-4">
            We sent a 4-digit code to{' '}
            <span className="font-medium text-gray-900">{value}</span>.{' '}
            <button
              type="button"
              onClick={onChangeEmail}
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Change
            </button>
          </div>
        )}

        <FormField
          className="text-xs"
          label="Password"
          id="password"
          error={errors.password?.message}
        >
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              {...passwordRegRest}
              onChange={(e) => {
                onPasswordRHFChange(e);
                setLocalPassword(e.target.value);
              }}
              onFocus={() => setShowRules(true)}
              onBlur={() => setShowRules(false)}
              className="h-fit pr-10"
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label="Toggle password visibility"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4 text-gray-400" />
              ) : (
                <Eye className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
          {(showRules || localPassword) && (
            <div className="flex flex-wrap gap-2 pt-1">
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.minLength
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                8+ chars
              </div>
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.upper
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                A-Z
              </div>
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.lower
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                a-z
              </div>
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.number
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                0-9
              </div>
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.symbol
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                symbol
              </div>
              <div
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  passwordRules.noSpaces
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                no spaces
              </div>
            </div>
          )}
        </FormField>

        <FormField
          className="text-xs"
          label="Re-enter password"
          id="confirmPassword"
          error={errors.confirmPassword?.message}
        >
          <div className="relative">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              {...passwordRegister('confirmPassword', {
                required: 'Please confirm your password',
                validate: (confirmValue, formValues) =>
                  confirmValue === formValues.password ||
                  'Passwords do not match',
              })}
              className="h-fit pr-10"
            />
            <button
              type="button"
              onClick={onToggleConfirmPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label="Toggle confirm password visibility"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-4 h-4 text-gray-400" />
              ) : (
                <Eye className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </FormField>

        <OTPVerificationSection
          type={type}
          isVerifyingOTP={isVerifyingOTP}
          handleVerifyOTP={handleVerifyOTP}
          otpError={otpError}
          canResend={canResend}
          resendTimeout={resendTimeout}
          handleSendOTP={handleSendOTP}
          verified={verified}
          currentOTP={currentOTP}
          otpFocusTrigger={otpFocusTrigger}
          submitButtonText={submitButtonText}
          onSubmit={onSubmit}
        />
      </div>
    );
  }

  // Default behavior for existing flows
  const otpBtnLabel = () => {
    if (isOTPLoading) return null; // spinner shown instead
    if (isOTPSent && !canResend) return `Resend in ${resendTimeout}s`;
    if (isOTPSent && canResend) return 'Resend OTP';
    return 'Get OTP';
  };

  const otpBtnClass = () => {
    let c = 'tb-otp-btn';
    if (isOTPSent && !canResend) c += ' counting';
    if (verified) c += ' sent';
    return c;
  };

  return (
    <div className="tb-field">
      {/* Label */}
      <label htmlFor={type} className="tb-label">
        {type === 'contact' ? (
          <>
            Mobile Number <span className="tb-req">*</span>
          </>
        ) : (
          <>
            {config[type].label} <span className="tb-req">*</span>
          </>
        )}
      </label>

      {/* Phone row */}
      {type === 'contact' ? (
        <div className="tb-phone-row">
          {/* Grouped phone input */}
          <div className="tb-phone-group-wrap">
            <div
              className={`tb-phone-group${errors[type] ? ' error' : ''}${disabled ? ' disabled' : ''}${verified ? ' disabled' : ''}`}
            >
              <div className="tb-phone-prefix">+91</div>
              <input
                id={type}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="98765 43210"
                onChange={registerProps.onChange}
                onBlur={registerProps.onBlur}
                name={registerProps.name}
                ref={registerProps.ref}
                disabled={disabled || verified}
                readOnly={readOnly}
                className="tb-phone-input"
                autoComplete="tel-national"
              />
              {verified && (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--ah-status-success)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ marginRight: 12, flexShrink: 0 }}
                >
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              )}
            </div>
          </div>

          {/* OTP button */}
          <button
            type="button"
            className={otpBtnClass()}
            onClick={() => handleSendOTP(isOTPSent ? 'RESEND' : 'SEND')}
            disabled={
              !value ||
              value.length !== 10 ||
              isOTPLoading ||
              disabled ||
              verified ||
              (!canResend && isOTPSent)
            }
          >
            {isOTPLoading ? (
              <span
                className="tb-spinner"
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
            ) : verified ? (
              'Verified ✓'
            ) : (
              otpBtnLabel()
            )}
          </button>
        </div>
      ) : (
        /* Email / other type */
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              id={type}
              type={type === 'email' ? 'email' : 'text'}
              onChange={registerProps.onChange}
              onBlur={registerProps.onBlur}
              name={registerProps.name}
              ref={registerProps.ref}
              placeholder={config[type].placeholder}
              disabled={disabled}
              readOnly={readOnly}
              className={`tb-input${errors[type] ? ' error' : ''}`}
            />
            {verified && (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--ah-status-success)"
                strokeWidth="2.5"
                strokeLinecap="round"
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            )}
          </div>
          <button
            type="button"
            className="tb-otp-btn"
            onClick={() => handleSendOTP('SEND')}
            disabled={!value || isOTPSent || isOTPLoading || disabled}
          >
            {isOTPLoading ? (
              <span
                className="tb-spinner"
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
            ) : (
              'Get OTP'
            )}
          </button>
        </div>
      )}

      {/* Validation error */}
      {errors[type]?.message && (
        <div className="tb-field-error">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{errors[type].message}</span>
        </div>
      )}

      {/* OTP boxes + resend logic */}
      {showOTPInput && (
        <OTPVerificationSection
          type={type}
          isVerifyingOTP={isVerifyingOTP}
          handleVerifyOTP={handleVerifyOTP}
          otpError={otpError}
          canResend={canResend}
          resendTimeout={resendTimeout}
          handleSendOTP={handleSendOTP}
          verified={verified}
          currentOTP={currentOTP}
          otpFocusTrigger={otpFocusTrigger}
        />
      )}
    </div>
  );
};
